import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core'
import { HttpClient } from '@angular/common/http';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DatabaseService } from '../../services/database/database.service';
import { FileData, AvailableSchemas, Database } from '../../../models/database';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, filter, finalize, take, takeUntil } from 'rxjs';
import { NgbModal, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { FileViewComponentComponent } from '../../shared/file-view-component/file-view-component.component';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from '../../services/toast/toast.service';
import { DataShareService } from '../../services/data_share/data-share.service';
import { LoadingServiceService } from '../../services/loadingService/loading-service.service';



@Component({
  selector: 'app-datasource-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbToastModule],
  templateUrl: './datasource-page.component.html',
  styleUrl: './datasource-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})


export class DatasourcePageComponent implements OnInit, OnDestroy {
  databaseName: Database[] = [];
  maxFiles: number = 10;
  files: File[] = [];
  dropdown: any = [];
  selectedDatabase: string | null = null;
  selectedPlatformType: string | null = null;
  uploadedFiles: FileData[] = [];
  availableDatabases: string[] = [];
  availableSchemas: AvailableSchemas = {};
  platformSelected: boolean = false;
  filesUploaded: boolean = false;
  private destroy$ = new Subject<void>();
  private initialLoad: boolean = true;
  private hasShownToastReset = false;
  private previousDatabaseSelection: string | null = null;


  @ViewChild('fileInput') fileInput!: ElementRef;

  database$: Observable<Database[]> | undefined;
  loadingDatabases: boolean = false;
  availableDatabases$ = new BehaviorSubject<string[]>([]);
  schemaLoadingStates: { [key: string]: boolean } = {}
  nextButtonDisabled: boolean = true;
  nextButtonText = 'Select Node'

  constructor(
    private router: Router,
    private databaseService: DatabaseService,
    private http: HttpClient,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private dataShare: DataShareService,
    public toastService: ToastService,
    public loadService: LoadingServiceService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { 
    this.router.events.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.url === '/datasrc' || event.urlAfterRedirects === '/datasrc') {
          this.checkForFlowReset();
        }
      }
    });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initialLoad = true;
      this.checkForFlowReset();
      this.loadService.show()
      this.listDatabases()
      this.selectedDatabase = 'None'
      this.updateButtonState();
      this.cdRef.detectChanges();
      this.initialLoad = false;
    }
  }

  private checkForFlowReset(): void {
    // Check if we're coming from any page in the flow
    const navigation = this.router.getCurrentNavigation();
    const previousUrl = navigation?.previousNavigation?.finalUrl?.toString();

    const flowPages = ['/tabview', '/outputstore', '/codereview'];
    const isReturningFromFlow = flowPages.some(page => previousUrl?.includes(page));

    if (isReturningFromFlow && !this.hasShownToastReset) {
      this.resetFlow();
      this.toastService.showToast('Starting New Data Generation Workflow', { classname: 'bg-warning text-dark' });
      this.hasShownToastReset = true;

      setTimeout(() => {
        this.hasShownToastReset = false;
      }, 1000)
    }
  }

  private resetFlow(): void {
    // Clear all stored data
    this.dataShare.clearData();

    // Reset component state
    this.selectedDatabase = 'None';
    this.selectedPlatformType = null;
    this.uploadedFiles = [];
    this.filesUploaded = false;
    this.platformSelected = false;
    this.nextButtonDisabled = true;
    this.nextButtonText = 'Select Node';

    // Reset file input if it exists
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    this.cdRef.detectChanges();
  }

  listDatabases(): void {
    // const sessionID = sessionStorage.getItem('Session_Id')
    this.databaseService.getDatabases().pipe(
      take(1),
      finalize(() => this.loadService.hide())
    ).subscribe((data) => {
      this.databaseName = data;
      this.cdRef.detectChanges();
    });
  }

  onFileSelected(event: any) {
    const selectedFiles: FileList = event.target.files;
    if (this.uploadedFiles.length + selectedFiles.length > this.maxFiles) {
      this.toastService.showToast(`You can only upload a maximum of ${this.maxFiles} files`, { classname: 'bg-info text-light' })
      return;
    }
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i]
      this.uploadedFiles.push({
        filename: file.name,
        size: file.size,
        file: file,
        database_name: '',
        schema_name: '',
        table_name: ''
      });
      // if (this.uploadedFiles.length >= 5) {
      //   this.toastService.showToast(`You are about to reach the maximum limit of ${this.maxFiles} files`, { classname: 'bg-info text-light' })
      // }
      if (this.uploadedFiles.length >= this.maxFiles - 2) {
        this.toastService.showToast(`Only ${this.maxFiles - this.uploadedFiles.length} more files can be added.`, { classname: 'bg-info text-light' })
      }
    }
    this.filesUploaded = this.uploadedFiles.length > 0;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = ''
    }
    this.updateButtonState()
    this.cdRef.detectChanges();
  }

  onDatabaseSelect(event: Event) {
    const selectedDBName = (event.target as HTMLSelectElement).value;
    // const previousSelection = this.selectedDatabase;
    this.selectedDatabase = selectedDBName

    if (selectedDBName === 'None' && !this.initialLoad && this.previousDatabaseSelection !== 'None'){
      this.toastService.showToast(`Please select a valid Platform Name`, { classname: 'bg-warning text-light' })
      // this.selectedPlatformType = null;
    } 
    // else {
    //   const selectedDB = this.databaseName.find(db => db.platform_name === selectedDBName)
    //   this.selectedPlatformType = selectedDB?.platform_type || null
    // }
    this.previousDatabaseSelection = this.selectedDatabase;
    this.selectedDatabase = selectedDBName;
    this.selectedPlatformType = selectedDBName === 'None' ? null : this.databaseName.find(db => db.platform_name === selectedDBName) ?.platform_type || null
    this.updateButtonState()
    this.cdRef.detectChanges();
  }

  public updateButtonState() {
    const hasValidDatabase = this.selectedDatabase && this.selectedDatabase !== 'None'
    const hasValidFiles = this.uploadedFiles.length > 0 && this.areFilesValid();

    this.nextButtonDisabled = !(hasValidDatabase || hasValidFiles)

    // if (this.selectedDatabase === 'None' && this.uploadedFiles.length === 0) {
    //   this.nextButtonDisabled = true; // Explicitly disable if BOTH are false
    //   this.nextButtonText = 'Select Platform or Upload Files';
    if (hasValidDatabase && !hasValidFiles) {
      // this.nextButtonDisabled = false; // Enable if platform is selected but no files
      this.nextButtonText = 'Proceed with Node';
    } else if (!hasValidDatabase && hasValidFiles) {
      // this.nextButtonDisabled = false; // Enable if files are uploaded but no platform
      this.nextButtonText = 'Proceed with Files';
    } else if (hasValidDatabase && hasValidFiles) {
      //  this.nextButtonDisabled = false; // Enable if BOTH are selected/uploaded
      this.nextButtonText = 'Next';
    } else {
      this.nextButtonText = 'Select Node';
    }
    this.cdRef.detectChanges(); // Ensure Change Detection
  }

  public areFilesValid(): boolean {
    if (this.uploadedFiles.length === 0) return false;

    // return this.uploadedFiles.every(file =>
    //   file.database_name && file.database_name.trim() !== '' &&
    //   file.schema_name && file.schema_name.trim() !== ''
    // )
    return this.uploadedFiles.every(file =>
      file.database_name.length >= 2 && !!file.database_name?.trim() &&
      file.schema_name.length >= 2 && !!file.schema_name?.trim()
    )
  }

  removeFile(index: number) {
    if (this.uploadedFiles && index >= 0 && index < this.uploadedFiles.length) {
      this.uploadedFiles.splice(index, 1)
      this.filesUploaded = this.uploadedFiles.length > 0
      this.updateButtonState();
      this.cdRef.detectChanges();
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
        this.cdRef.detectChanges()
      }
    } else {
      console.error("Invalid Index passed to remove the file")
    }
  }

  viewFile(file: FileData) {
    if (file && file.file) {
      const modalRef = this.modalService.open(FileViewComponentComponent, {
        size: 'xl',
        backdrop: 'static',
        keyboard: false
      });
      modalRef.componentInstance.file = file.file
      this.cdRef.detectChanges();
    }
  }

  private handleSnowflakeWithoutFiles(): void {
    // Proceed with Snowflake data (no files uploaded)
    this.dataShare.updateSelectedDatabase(this.selectedDatabase);
    this.dataShare.updateSelectedDatabaseType(this.selectedPlatformType)
    this.router.navigate(['/tabview'], { queryParams: { db: this.selectedDatabase, typeOfDB: this.selectedPlatformType } });
  }

  private handleFilesWithoutSnowflake(): void {
    // Upload files and proceed (non-Snowflake selected)
    const filesToUpload = this.uploadedFiles.map(fileData => fileData.file);
    const metadata = this.uploadedFiles.map(file => ({
      filename: file.filename,
      database_name: file.database_name,
      schema_name: file.schema_name
    }));

    console.log("Metadata is : ", metadata);
    this.loadService.show()
    // const sessionID = sessionStorage.getItem('Session_Id')
    this.databaseService.uploadFiles(filesToUpload, metadata).pipe(
      finalize(() => this.loadService.hide())
    ).subscribe({
      next: (response) => {
        this.toastService.showToast("Files uploaded successfully without Snowflake Database", { classname: 'bg-success text-light' });
        this.dataShare.setUploadResponse(response);
        this.dataShare.updateUploadedFiles(this.uploadedFiles);
        this.router.navigate(['/tabview']) //, { queryParams: { db: this.selectedDatabase, typeOfDB: this.selectedPlatformType } };
      },
      error: (error) => {
        this.toastService.showToast('File upload failed', { classname: 'bg-danger text-light' });
        console.log("Error :", error)
      }
    });
  }

  private handleSnowflakeWithFiles(): void {
    // Handle both Snowflake and files (upload files & use Snowflake data)
    const filesToUpload = this.uploadedFiles.map(fileData => fileData.file);
    const metadata = this.uploadedFiles.map(file => ({
      filename: file.filename,
      database_name: file.database_name,
      schema_name: file.schema_name
    }));

    this.loadService.show()
    // const sessionID = sessionStorage.getItem('Session_Id')
    this.databaseService.uploadFiles(filesToUpload, metadata).pipe(
      finalize(() => this.loadService.hide())
    ).subscribe({
      next: (response) => {
        this.toastService.showToast("Files uploaded successfully with Snowflake data", { classname: 'bg-success text-light' });
        this.dataShare.setUploadResponse(response);
        this.dataShare.updateSelectedDatabase(this.selectedDatabase);
        this.dataShare.updateSelectedDatabaseType(this.selectedPlatformType)
        this.dataShare.updateUploadedFiles(this.uploadedFiles);
        // Include any additional Snowflake data handling here
        this.router.navigate(['/tabview'], { queryParams: { db: this.selectedDatabase, typeOfDB: this.selectedPlatformType } });
      },
      error: (error) => {
        this.toastService.showToast('File upload failed', { classname: 'bg-danger text-light' });
        console.log("Error :", error)
      }
    });
  }

  dbWorkflow() {

    const hasValidDatabase = this.selectedDatabase && this.selectedDatabase !== 'None'
    const hasValidFiles = this.uploadedFiles.length > 0 && this.areFilesValid();

    if(!hasValidDatabase && !hasValidFiles) {
      this.toastService.showToast('Please select a platform or upload valid files', {classname: 'bg-dange text-light'})
      return;
    }

    if (this.uploadedFiles.length > 0 && !this.areFilesValid()){
      // this.toastService.showToast('Please provide database and schema names for all files', {classname: 'bg-dange text-light'})
      // return;
      const invalidFiles = this.uploadedFiles.filter(f => !f.database_name?.trim() || !f.schema_name?.trim())
      this.toastService.showToast(`Please provide database and schema names for ${invalidFiles.length} file(s)`, {classname: 'bg-danger text-light'})
      return;
    }

    if (this.selectedDatabase !== 'None' && this.selectedPlatformType === 'snowflake') {
      if(hasValidFiles){
        this.handleSnowflakeWithFiles();
      }else {
        this.handleSnowflakeWithoutFiles();
      }
    } else if (hasValidFiles){
      this.handleFilesWithoutSnowflake();
    }
    
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

